<script lang="ts" setup>
	import { useQueryClient } from '@tanstack/vue-query'
	import useVuelidate from '@vuelidate/core'
	import {
		alpha,
		helpers,
		maxLength,
		minLength,
		numeric,
		required,
	} from '@vuelidate/validators'
	import { useDebounceFn } from '@vueuse/core'
	import { computed, onMounted, reactive, ref, watch } from 'vue'
	import { GoogleMap, Marker } from 'vue3-google-map'
	import { Collapse } from 'vue-collapsed'
	import { useToast } from 'vue-toastification'

	import AlertComponent from '@/components/AlertComponent.vue'
	import HasPermission from '@/components/HasPermission.vue'
	import ShadowBox from '@/components/ShadowBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import CoincidencesList from '@/internal/modules/providers/components/CoincidencesList.vue'
	import useAmadeusHotelSearch from '@/internal/modules/providers/composables/useAmadeusHotelSearch.ts'
	import useHotelForm from '@/internal/modules/providers/composables/useHotelForm'
	import { Hotel } from '@/internal/modules/providers/interfaces/Hotel'
	import useSearchBrands from '@/internal/modules/providers/pages/hotel/brands/composables/useSearchBrands'
	import useSearchHotelGroups from '@/internal/modules/providers/pages/hotel/group/composables/useSearchHotelGroups'

	interface Props {
		hotel: Hotel
	}

	const props = defineProps<Props>()
	defineEmits(['update:title'])

	const { hasPermissionUser } = useHasPermissionUser()
	const toast = useToast()

	const editCodeAmadeus = ref(false)

	const {
		data: amadeusHotels,
		filters,
		cityCode,
		enable,
		isLoading,
		isRefetching,
		remove,
		refetch,
		updateHotelMutation,
	} = useAmadeusHotelSearch({
		queryKey: props.hotel.id.toString(),
		hotelId: props.hotel.id,
	})

	const localHotels = ref()
	watch(amadeusHotels, (value) => {
		if (value) {
			localHotels.value = value
		}
	})

	const {
		form,
		handleEvaluationUpdate,
		handleUpdate,
		isCoincidence,
		hotels,
		updateHotelLoading,
	} = useHotelForm(props.hotel)

	const hoverRating = ref(0)

	const {
		data: brands,
		isLoading: loadingBrands,
		setQuery: setQueryBrand,
	} = useSearchBrands({
		immediate: true,
		queryKey: 'list-brands',
		errorHandling: false,
	})

	const {
		data: hotelGroups,
		isLoading: loadingHotelGroups,
		setQuery: setQueryHotelGroups,
	} = useSearchHotelGroups({
		immediate: true,
		queryKey: 'hotel-groups',
		errorHandling: false,
	})

	const rules = computed(() => ({
		cityCode: {
			required: helpers.withMessage(
				'El codigo de ciudad es requerido',
				required
			),
			minLength: helpers.withMessage(
				'El codigo de ciudad debe de ser 3 caracteres como minimo',
				minLength(3)
			),
			maxLength: helpers.withMessage(
				'El codigo de ciudad debe de ser 3 caracteres como maximo',
				maxLength(3)
			),
			alpha: helpers.withMessage(
				'El codigo solo debe de contener letras',
				alpha
			),
		},
		q: {
			alpha,
		},
		month: {
			numeric,
		},
	}))

	const v$ = useVuelidate(rules, {
		cityCode,
		q: filters.q,
		month: filters.months,
	})
	const queryClient = useQueryClient()
	const searching = ref(false)

	const debaunceLoading = ref(false)

	const updateHotel = useDebounceFn(async () => {
		debaunceLoading.value = true
		try {
			await handleEvaluationUpdate()
		} finally {
			debaunceLoading.value = false
		}
	}, 1000)

	const searchHotel = useDebounceFn(async () => {
		await v$.value.$validate()
		if (!v$.value.$error) {
			enable.value = true
			refetch()
		}
	}, 1000)

	const openSearchForm = ref(false)
	const confirm = ref(false)
	const selectedHotel = reactive({
		code: '',
		name: '',
	})

	const handleSetCode = async () => {
		const { code } = selectedHotel
		if (code != '') {
			await updateHotelMutation.mutateAsync(
				{
					hotelId: props.hotel.id,
					code,
				},
				{
					onSuccess: async () => {
						confirm.value = false
						selectedHotel.code = ''
						selectedHotel.name = ''
						remove()
						v$.value.$reset()
						localHotels.value = []
						toast.success('Asignado correctamente')
						queryClient.invalidateQueries(['hotel', props.hotel.id])
						queryClient.refetchQueries(['hotel', props.hotel.id])
						openSearchForm.value = false
					},
					onError(error, variables, context) {
						toast.error(error as string)
					},
				}
			)
		}
	}

	const loading = computed(
		() => enable.value && (isLoading.value || isRefetching.value)
	)

	const loadingUpadte = computed(
		() => debaunceLoading.value || updateHotelLoading.value
	)

	const mapView = ref(false)
</script>

<template>
	<ShadowBox class="bg-white h-full w-full">
		<h1 class="text-2xl pb-3 font-bold">Información general</h1>
		<form @submit.prevent="handleUpdate()">
			<div class="grid grid-cols-1 md:grid-cols-2 gap-2 md:pb-2">
				<StyledInput
					v-model="form.name"
					label="Nombre del Hotel"
					placeholder="Nombre del hotel"
					class=""
					:is-required="true"
					:disabled="isCoincidence"
				/>

				<div class="flex gap-2 items-center pt-5">
					<p class="text-gray-400">Calificación</p>
					<template
						v-for="star in 5"
						:key="star"
					>
						<button
							type="button"
							@mouseover="hoverRating = star"
							@mouseleave="hoverRating = 0"
							@click="form.rating = star"
							class="material-symbols-outlined"
						>
							<span
								:class="[
									hoverRating >= star || form.rating >= star ?
										'text-yellow-500'
									:	'',
									'text-xl transition-all',
								]"
								>star</span
							>
						</button>
					</template>
				</div>
			</div>

			<div class="grid grid-cols-1 md:grid-cols-2 gap-2">
				<StyledCombobox
					v-model="form.brand"
					placeholder="No aplica"
					class="flex-1 mb-3"
					:options="brands?.data || []"
					:loading-options="loadingBrands"
					:on-input-change="
						(value) => {
							setQueryBrand(value)
						}
					"
					:show-input="!form.brand"
					label="Nombre de la marca"
					:is-required="true"
					v-if="hasPermissionUser('viewAny-internal', 'App\\Models\\Brand')"
				>
					<template #default="{ option }">
						<span>{{ option.name }}</span>
					</template>

					<template #input-overlap>
						<template v-if="form.brand">
							<span>{{ form.brand.name }}</span>

							<button
								type="button"
								class="material-symbols-outlined text-[22px]"
								@click.prevent="form.brand = undefined"
							>
								close
							</button>
						</template>
					</template>
				</StyledCombobox>

				<StyledCombobox
					v-model="form.group"
					placeholder="No aplica"
					class="flex-1 mb-3"
					:options="hotelGroups?.data || []"
					:loading-options="loadingHotelGroups"
					:on-input-change="setQueryHotelGroups"
					:show-input="!form.group"
					label="Nombre del grupo"
					:is-required="true"
					v-if="hasPermissionUser('viewAny-internal', 'App\\Models\\Group')"
				>
					<template #default="{ option }">
						<span>{{ option.name }}</span>
					</template>

					<template #input-overlap>
						<template v-if="form.group">
							<span>{{ form.group.name }}</span>

							<button
								type="button"
								class="material-symbols-outlined text-[22px]"
								@click.stop="form.group = undefined"
							>
								close
							</button>
						</template>
					</template>
				</StyledCombobox>
			</div>

			<StyledButton
				v-if="!isCoincidence"
				type="submit"
				class="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded ml-auto"
				:loading="loadingUpadte"
				:disabled="loadingUpadte"
			>
				Guardar cambios
			</StyledButton>

			<CoincidencesList
				v-if="isCoincidence"
				:data="hotels?.data || []"
				:isCoincidence="isCoincidence"
				:loadingCreating="loadingUpadte"
				:handle-action="handleUpdate"
				:clear-state="
					() => {
						isCoincidence = false
					}
				"
			/>
			<hr class="my-4" />
		</form>

		<form
			@submit.prevent="searchHotel"
			class="grid grid-cols-1 md:grid-cols-2 gap-2"
		>
			<template v-if="props.hotel.amadeusCode">
				<div
					class="grid grid-cols-1 md:grid-cols-5 col-span-2"
					v-if="!openSearchForm"
				>
					<div class="h-80 w-80 aspect-square col-span-1 md:col-span-2">
						<img
							v-if="hotel.frontView"
							class="h-full rounded-lg object-cover object-center"
							:src="hotel.frontView"
							:alt="'imagen del hotel ' + hotel.name"
						/>
						<div
							v-else
							class="bg-slate-400 h-full rounded-lg w-full"
						></div>
					</div>

					<div class="col-span-2 gap-2 flex flex-col mt-2">
						<h3 class="font-bold text-xl uppercase">
							{{ hotel.amadeusName }}
						</h3>

						<h3 class="font-semibold text-sm">
							Código de Amadeus:
							<span class="font-bold text-sm">{{ hotel.amadeusCode }}</span>
						</h3>

						<h3 class="font-semibold text-sm">
							Dirección:
							<span class="font-bold text-sm">{{ hotel.address }}</span>
						</h3>

						<StyledButton
							class="bg-blue-500 hover:bg-blue-700 text-white"
							@click="mapView = true"
							type="button"
						>
							<span class="material-symbols-outlined"> pin_drop </span>
							Ver ubicación
						</StyledButton>
					</div>

					<div class="flex items-start justify-end">
						<StyledButton
							class="bg-blue-500 hover:bg-blue-700 text-white"
							@click.prevent="
								() => {
									openSearchForm = !openSearchForm
								}
							"
						>
							Editar Codigo
						</StyledButton>
					</div>
				</div>
			</template>
			<div
				v-else
				class="col-span-2"
			>
				<div
					class="w-full flex flex-col items-center"
					v-if="!openSearchForm && !loading"
				>
					<span class="material-symbols-outlined text-7xl text-gray-500"
						>no_luggage</span
					>
					<p class="mt-1 text-sm text-gray-500">
						No se ha asignado ningun codigo de Amadeus a este hotel
					</p>
					<styled-button
						class="bg-green-600 hover:bg-green-700 text-white my-3"
						@click.prevent="
							() => {
								openSearchForm = true
							}
						"
					>
						Buscar Hotel en Amadeus
					</styled-button>
				</div>
			</div>

			<Collapse
				as="section"
				:when="openSearchForm"
				class="v-collapse col-span-2"
			>
				<div class="grid grid-cols-1 md:grid-cols-2 gap-2 pb-3">
					<StyledInput
						label="Nombre a buscar"
						:value="hotel.name"
						v-model="filters.q"
						class=""
						:is-required="false"
						:error="v$.q.$silentErrors.at(0)?.$message"
					/>
					<StyledInput
						label="Codigo de Ciudad"
						placeholder="MTY"
						v-model="cityCode"
						:is-required="true"
						:error="v$.cityCode.$errors.at(0)?.$message"
					/>
				</div>
				<div class="flex">
					<styled-button
						type="submit"
						class="text-white mr-3"
						:loading="loading"
						:class="[
							loading ? 'bg-green-700' : 'bg-green-600 hover:bg-green-700',
						]"
						:disabled="loading"
					>
						Buscar
					</styled-button>
					<styled-button
						type="button"
						class="bg-red-500 hover:bg-red-600 text-white"
						@click.prevent="
							async () => {
								await remove()
								localHotels = []
								v$.$reset()
								openSearchForm = false
							}
						"
					>
						Cancelar Busqueda
					</styled-button>
				</div>

				<div
					class="overflow-y-scroll h-96 mt-5 snap-y snap-proximity"
					v-if="localHotels?.length > 0"
				>
					<shadow-box
						class="group my-3 grid grid-cols-1 md:grid-cols-5 hover:bg-slate-200 transition-all snap-center"
						v-for="hotel in localHotels"
						:key="hotel.id"
					>
						<div
							class="h-40 md:w-auto aspect-square group-hover:scale-105 flex items-center justify-center"
						>
							<img
								v-if="hotel.image"
								class="h-full rounded-lg"
								:src="hotel.image"
								alt="imagen del hotel"
							/>
							<div
								v-else
								class="bg-slate-400 h-full rounded-lg w-full"
							></div>
						</div>
						<div class="py-3 md:pl-5 pl-14 flex flex-col col-span-3">
							<h3 class="font-bold text-xl">{{ hotel.name }}</h3>
							<p>{{ hotel.chain }}</p>
							<p>{{ hotel.addressLine }}</p>
						</div>
						<div class="flex items-center justify-center">
							<styled-button
								class="bg-blue-500 text-white"
								@click.prevent="
									() => {
										confirm = true
										selectedHotel.code = hotel.code
										selectedHotel.name = hotel.name
									}
								"
								>Seleccionar hotel</styled-button
							>
						</div>
					</shadow-box>
				</div>

				<div
					v-if="amadeusHotels?.length == 0"
					class="m-3 relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
				>
					<span class="mt-2 block text-sm font-semibold text-gray-900"
						>No se encontraron resultados</span
					>
				</div>
			</Collapse>
		</form>
	</ShadowBox>

	<AlertComponent
		v-model="confirm"
		buttons
		dismiss
		type="info"
		@confirm="handleSetCode"
	>
		<template v-slot:title>Relacionar hotel con amadeus</template>
		<template v-slot:body>
			se relacionara el hotel {{ selectedHotel.name }} de amadeus con el hotel
			{{ props.hotel.name }}
		</template>
		<template v-slot:closeButton> cancelar </template>
		<template v-slot:button=""> Aceptar </template>
	</AlertComponent>

	<div
		v-if="mapView"
		class="fixed top-0 bottom-0 left-0 right-0 z-50"
	>
		<div class="relative p-4">
			<div
				class="relative z-10 flex items-center justify-center gap-3 text-white"
			>
				<p class="font-bold">{{ hotel.name }}</p>
				<span
					class="material-symbols-outlined cursor-pointer"
					@click="mapView = false"
				>
					close
				</span>
			</div>

			<div class="absolute inset-0 bg-blue-ef-400"></div>
		</div>

		<GoogleMap
			:map-type-control="false"
			:fullscreen-control="false"
			:street-view-control="false"
			api-key="AIzaSyDLM7Yvm2hjh8jzg62FVn1GzKm2RfFesxg"
			style="width: 100%; height: 100%"
			:center="{
				lat: parseFloat(hotel.latitude),
				lng: parseFloat(hotel.longitude),
			}"
			:zoom="15"
			:clickable-icons="false"
			:heading="320"
			:tilt="47.5"
		>
			<Marker
				:options="{
					position: {
						lat: parseFloat(hotel.latitude),
						lng: parseFloat(hotel.longitude),
					},
				}"
			/>
		</GoogleMap>
	</div>
</template>
